'use client'

import { Badge } from '@/components/v2/ui/badge'
import { Button } from '@/components/v2/ui/button'
import { Checkbox } from '@/components/v2/ui/checkbox'
import { DialogClose } from '@/components/v2/ui/dialog'
import { Input } from '@/components/v2/ui/input'
import { Label } from '@/components/v2/ui/label'
import { Textarea } from '@/components/v2/ui/textarea'
import { useTexts } from '@/contexts/LanguageContext'
import { cn } from '@/lib/utils'
import { type IRequestAccessFields, type IRequestAccessFormState, isValidEmail } from './useRequestAccessForm'

const LABEL_CLASS = 'text-xs font-medium text-trust-center-muted'

interface IFieldProps {
  field: keyof Omit<IRequestAccessFields, 'reason'>
  label: string
  placeholder: string
  state: IRequestAccessFormState
  className?: string
}

function Field({ field, label, placeholder, state, className }: IFieldProps) {
  const value = state.fields[field]
  const isEmail = field === 'email'
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={field} className={LABEL_CLASS}>
        {label}
      </Label>
      <Input
        id={field}
        type={isEmail ? 'email' : 'text'}
        value={value}
        placeholder={placeholder}
        aria-invalid={isEmail && Boolean(value) && !isValidEmail(value)}
        onChange={(event) => state.setField(field, event.target.value)}
      />
    </div>
  )
}

function DocumentOptions({ options, state }: { options: string[]; state: IRequestAccessFormState }) {
  const { PUBLIC_TRUST_CENTER } = useTexts()
  return (
    <div className="flex flex-col gap-2.5">
      <span className={LABEL_CLASS}>{PUBLIC_TRUST_CENTER.REQUEST_ACCESS.DOCUMENTS}</span>
      <div className="-mx-2 flex flex-col gap-0.5">
        {options.map((option) => (
          <Label
            key={option}
            className="cursor-pointer rounded px-2 py-1.5 text-sm font-normal hover:bg-trust-center-subtle"
          >
            <Checkbox checked={state.selected.includes(option)} onCheckedChange={() => state.toggle(option)} />
            <span className="flex-1">{option}</span>
            <Badge
              variant="outline"
              className="rounded-full border-trust-center-border-strong text-trust-center-accent"
            >
              {PUBLIC_TRUST_CENTER.RESOURCES.PRIVATE}
            </Badge>
          </Label>
        ))}
      </div>
    </div>
  )
}

function Fields({ options, state }: { options: string[]; state: IRequestAccessFormState }) {
  const { REQUEST_ACCESS } = useTexts().PUBLIC_TRUST_CENTER
  return (
    <div className="flex flex-col gap-4.5 p-5">
      <div className="grid grid-cols-2 gap-3.5">
        <Field
          field="name"
          label={REQUEST_ACCESS.FULL_NAME}
          placeholder={REQUEST_ACCESS.FULL_NAME_PLACEHOLDER}
          state={state}
        />
        <Field
          field="email"
          label={REQUEST_ACCESS.EMAIL}
          placeholder={REQUEST_ACCESS.EMAIL_PLACEHOLDER}
          state={state}
        />
        <Field
          field="company"
          label={REQUEST_ACCESS.COMPANY}
          placeholder={REQUEST_ACCESS.COMPANY_PLACEHOLDER}
          state={state}
          className="col-span-2"
        />
      </div>
      <DocumentOptions options={options} state={state} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reason" className={LABEL_CLASS}>
          {REQUEST_ACCESS.REASON}
        </Label>
        <Textarea
          id="reason"
          value={state.fields.reason}
          placeholder={REQUEST_ACCESS.REASON_PLACEHOLDER}
          onChange={(event) => state.setField('reason', event.target.value)}
        />
      </div>
    </div>
  )
}

export default function RequestAccessForm({ options, state }: { options: string[]; state: IRequestAccessFormState }) {
  const { REQUEST_ACCESS } = useTexts().PUBLIC_TRUST_CENTER
  return (
    <>
      <Fields options={options} state={state} />
      <div className="flex items-center gap-2 border-t border-trust-center-border px-5 py-3.5">
        <span className="flex-1 text-xs text-trust-center-muted">
          {state.isValid ? REQUEST_ACCESS.READY : REQUEST_ACCESS.REQUIRED}
        </span>
        <DialogClose asChild>
          <Button variant="outline" className="shadow-none">
            {REQUEST_ACCESS.CANCEL}
          </Button>
        </DialogClose>
        <Button
          disabled={!state.isValid || state.isSending}
          onClick={() => void state.submit()}
          className="bg-trust-center text-white hover:bg-trust-center-deep"
        >
          {REQUEST_ACCESS.SUBMIT}
        </Button>
      </div>
    </>
  )
}
